import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  inject,
  output,
  signal,
} from '@angular/core';
import { ControlValueAccessor, FormControl } from '@angular/forms';
import { BehaviorSubject, Subject } from 'rxjs';

import { getColorStates } from '@plugins/client/colors/color-utils';
import { DECORATIVE_ELEMENT_MIN_CONTRAST } from '@plugins/client/colors/constants';
import { ContrastColorValues } from '@plugins/client/colors/types';
import { Maybe } from '@plugins/shared/types';

import { FormControlComponent } from '../form-control/form-control.component';
import { setControlRelationship } from '../set-control-relationship/set-control-relationship';

@Component({
  selector: 'plugins-form-base-control',
  standalone: true,
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormBaseControlComponent<T = unknown>
  extends FormControlComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  @Input() set color(val: Maybe<string>) {
    this.setColors(val ?? undefined);
  }

  /** Custom color contrast config to affect how the contrast
   * color check is handled on the color input. */
  @Input() colorContrastConfig?: Partial<ContrastColorValues>;
  @Input() inputClass?: string;
  @Input() inputStyle?: Record<string, string | number>;
  @Input() role?: string;
  @Input() ariaLabel?: string;
  @Input() ariaLabelledBy?: string;
  @Input() errorId?: string;

  readonly onfocus = output<void>();
  readonly onblur = output<void>();

  public cdRef = inject(ChangeDetectorRef);

  control = new FormControl<T | null>(null);
  baseColor = '#000000';
  focusColor = 'rgba(0, 0, 0, 0.2)';
  protected readonly providedColor = signal<string | undefined>(undefined);
  private _disabled$ = new BehaviorSubject<boolean>(false);
  disabled$ = this._disabled$.asObservable();
  private _focused$ = new BehaviorSubject<boolean>(false);
  focused$ = this._focused$.asObservable();
  private _hovered$ = new BehaviorSubject<boolean>(false);
  hovered$ = this._hovered$.asObservable();
  protected destroy$ = new Subject<void>();

  get disabled() {
    return this._disabled$.value;
  }

  get focused() {
    return this._focused$.value;
  }

  get hovered() {
    return this._hovered$.value;
  }

  constructor() {
    super();
    if (this.controlDirective) {
      this.controlDirective.valueAccessor = this;
    }
  }

  onTouched!: () => void;
  onChange!: (value: unknown) => void;

  override ngOnInit(): void {
    super.ngOnInit();
    this.setControlRelationship();
  }

  setControlRelationship() {
    setControlRelationship({
      controlDirective: this.controlDirective,
      formControl: this.control,
      destroy$: this.destroy$,
      onChange: this.onChange,
      emitEvent: true,
      changeDetectorRef: this.cdRef,
      writeValueToParent: true,
      inheritValidators: true,
    });
  }

  setColors(color: string | undefined): void {
    const { baseColor, focusColor } = getColorStates(color, {
      contrastRatio: DECORATIVE_ELEMENT_MIN_CONTRAST,
      ...this.colorContrastConfig,
    });
    this.providedColor.set(color);
    this.baseColor = baseColor;
    this.focusColor = focusColor;
  }

  generateStyle(
    style: Record<string, string | number | undefined>,
  ): Record<string, string | number | undefined> {
    if (!this.inputStyle) {
      return style;
    }
    return Object.assign(style, this.inputStyle);
  }

  writeValue(value: T): void {
    this.control.patchValue(value);
    this.cdRef.detectChanges();
  }

  registerOnChange(fn: (val: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.control.disable({ emitEvent: false });
    } else {
      this.control.enable({ emitEvent: false });
    }

    this._disabled$.next(isDisabled);
    this.cdRef.markForCheck();
  }

  handleFocus(): void {
    this._focused$.next(true);
    this.onfocus.emit();
  }

  handleBlur(): void {
    this._focused$.next(false);
    this.onblur.emit();
  }

  handleMouseEnter(): void {
    this._hovered$.next(true);
  }

  handleMouseLeave(): void {
    this._hovered$.next(false);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
